import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { 
  LogOut, 
  Search, 
  Download, 
  Eye, 
  Calendar,
  User,
  Building,
  Mail,
  Phone,
  Globe,
  FileText,
  TrendingUp,
  Target,
  Users,
  Clock,
  Trash2,
  MessageSquare,
  MapPin,
  Linkedin
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, token, onLogout }) => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    received: 0,
    contacted: 0,
    interview_pending: 0,
    in_review: 0,
    approved: 0,
    rejected: 0,
    recent: 0
  });
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarksData, setRemarksData] = useState({ applicationId: "", status: "", remarks: "" });

  useEffect(() => {
    fetchApplications();
    fetchStats();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        onLogout();
      } else {
        toast.error('Failed to load applications');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.startupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.founderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.founderEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    setFilteredApplications(filtered);
  };

  const updateApplicationStatus = async (applicationId, newStatus, remarks = "") => {
    // For approved/rejected status, show remarks modal
    if ((newStatus === 'approved' || newStatus === 'rejected') && !remarks) {
      setRemarksData({ applicationId, status: newStatus, remarks: "" });
      setShowRemarksModal(true);
      return;
    }

    try {
      await axios.put(`${API}/admin/applications/${applicationId}/status`, 
        { status: newStatus, remarks: remarks || undefined },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Update local state
      setApplications(apps => 
        apps.map(app => 
          app.id === applicationId ? { ...app, status: newStatus, remarks: remarks || app.remarks } : app
        )
      );
      
      // Refresh stats
      fetchStats();
      
      toast.success(`Application marked as ${newStatus}`);
      setShowRemarksModal(false);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update application status');
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/applications/${applicationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Remove from local state
      setApplications(apps => apps.filter(app => app.id !== applicationId));
      
      // Refresh stats
      fetchStats();
      
      toast.success('Application deleted successfully');
    } catch (error) {
      console.error('Failed to delete application:', error);
      toast.error('Failed to delete application');
    }
  };

  const handleRemarksSubmit = () => {
    updateApplicationStatus(remarksData.applicationId, remarksData.status, remarksData.remarks);
  };

  const exportToCSV = () => {
    const headers = [
      'Startup Name', 'Founder Name', 'Email', 'Phone', 'Location', 'Current Stage', 
      'Team Size', 'Submitted At', 'Status', 'Remarks'
    ];
    
    const csvData = filteredApplications.map(app => [
      app.startupName || '',
      app.founderName || '',
      app.founderEmail || '',
      app.founderPhone || 'Not provided',
      app.location || 'Not provided',
      app.currentStage || '',
      app.teamSize || 'Not provided',
      new Date(app.submittedAt).toLocaleDateString(),
      app.status || 'submitted',
      app.remarks || ''
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `applications_${new Date().toISOString().split('T')[0]}.csv`);
    a.click();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received':
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-purple-100 text-purple-800';
      case 'interview_pending': return 'bg-orange-100 text-orange-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved':
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="/logo.png"
              alt="Modern Innovation Hub"
              className="h-8 w-auto"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome back, {user.name}</p>
            </div>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">In Review</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.in_review}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Approved</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.approved}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">This Week</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.recent}</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col md:flex-row gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search applications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="submitted">Submitted (Legacy)</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="interview_pending">Interview Pending</SelectItem>
                    <SelectItem value="in_review">In Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={exportToCSV} className="flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card>
          <CardHeader>
            <CardTitle>Applications ({filteredApplications.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Startup</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Founder</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Stage</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Submitted</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-semibold text-gray-900">{app.startupName}</p>
                          <p className="text-sm text-gray-600">{app.founderEmail}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <p className="text-gray-900">{app.founderName}</p>
                        {app.founderPhone && (
                          <p className="text-sm text-gray-600">{app.founderPhone}</p>
                        )}
                        {!app.founderPhone && (
                          <p className="text-xs text-gray-400">No phone provided</p>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant="outline">{app.currentStage}</Badge>
                      </td>
                      <td className="py-3 px-2">
                        <p className="text-sm text-gray-600">
                          {new Date(app.submittedAt).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="py-3 px-2">
                        <Badge className={getStatusColor(app.status)}>
                          {app.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedApplication(app)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {app.remarks && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => alert(`Remarks: ${app.remarks}`)}
                              title="View Remarks"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                          )}
                          <Select 
                            value={app.status} 
                            onValueChange={(value) => updateApplicationStatus(app.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="received">Received</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="interview_pending">Interview Pending</SelectItem>
                              <SelectItem value="in_review">In Review</SelectItem>
                              <SelectItem value="approved">Approved</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteApplication(app.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Application Details Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Application Details</h2>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedApplication(null)}
                >
                  Close
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Building className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Startup Name</p>
                      <p className="font-semibold">{selectedApplication.startupName}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Founder</p>
                      <p className="font-semibold">{selectedApplication.founderName}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{selectedApplication.founderEmail}</p>
                    </div>
                  </div>
                  
                  {selectedApplication.founderPhone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-semibold">{selectedApplication.founderPhone}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedApplication.location && (
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-semibold">{selectedApplication.location}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedApplication.founderLinkedIn && (
                    <div className="flex items-center space-x-3">
                      <Linkedin className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">LinkedIn</p>
                        <a 
                          href={selectedApplication.founderLinkedIn} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-semibold text-blue-600 hover:underline"
                        >
                          {selectedApplication.founderLinkedIn}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {selectedApplication.websiteUrl && (
                    <div className="flex items-center space-x-3">
                      <Globe className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Website</p>
                        <a 
                          href={selectedApplication.websiteUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-semibold text-blue-600 hover:underline"
                        >
                          {selectedApplication.websiteUrl}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  {selectedApplication.coFounderName && (
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Co-founder</p>
                        <p className="font-semibold">{selectedApplication.coFounderName}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedApplication.coFounderLinkedIn && (
                    <div className="flex items-center space-x-3">
                      <Linkedin className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Co-founder LinkedIn</p>
                        <a 
                          href={selectedApplication.coFounderLinkedIn} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-semibold text-blue-600 hover:underline"
                        >
                          {selectedApplication.coFounderLinkedIn}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Current Stage</p>
                      <Badge variant="outline">{selectedApplication.currentStage}</Badge>
                    </div>
                  </div>
                  
                  {selectedApplication.teamSize && (
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Team Size</p>
                        <p className="font-semibold">{selectedApplication.teamSize}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Submitted</p>
                      <p className="font-semibold">
                        {new Date(selectedApplication.submittedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Startup Idea Description</h3>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                    {selectedApplication.ideaDescription}
                  </p>
                </div>
                
                {selectedApplication.traction && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Current Traction</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.traction}
                    </p>
                  </div>
                )}
                
                {selectedApplication.whyWayanad && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Why Wayanad Program?</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.whyWayanad}
                    </p>
                  </div>
                )}
                
                {selectedApplication.expectedOutcome && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Expected Outcome</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.expectedOutcome}
                    </p>
                  </div>
                )}
                
                {selectedApplication.expectationFromProgram && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Expectation From Program</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.expectationFromProgram}
                    </p>
                  </div>
                )}
                
                {selectedApplication.remarks && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Admin Remarks</h3>
                    <p className="text-gray-700 bg-amber-50 p-4 rounded-lg border border-amber-200">
                      {selectedApplication.remarks}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remarks Modal */}
      {showRemarksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Add Remarks for {remarksData.status === 'approved' ? 'Approval' : 'Rejection'}
            </h3>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={4}
              placeholder="Enter remarks..."
              value={remarksData.remarks}
              onChange={(e) => setRemarksData({...remarksData, remarks: e.target.value})}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowRemarksModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemarksSubmit}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              >
                Save & Update Status
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;